import React from "react";
import { motion } from "framer-motion";
import { Star, ShieldCheck, Sparkles, ArrowUpRight } from "lucide-react";
import { PlayBadge } from "./PlayBadge";
import { LANDING } from "@/constants/testIds";
import { APP_SCREENSHOTS } from "@/lib/constants";

export const Hero = () => {
  return (
    <section
      id="top"
      className="relative pt-28 md:pt-36 pb-16 md:pb-24 overflow-hidden"
    >
      {/* Background ornaments */}
      <div className="absolute -top-40 -right-40 h-[560px] w-[560px] rounded-full bg-[#FEF0EB] blur-3xl opacity-70 pointer-events-none" />
      <div className="absolute top-1/2 -left-32 h-[300px] w-[300px] rounded-full bg-[#FEF0EB] blur-3xl opacity-50 pointer-events-none" />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        {/* Left content */}
        <motion.div
          className="lg:col-span-7"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
        >
          <div className="inline-flex items-center gap-2 rounded-full bg-white border border-[#E5E5E0] px-3.5 py-1.5 text-xs font-semibold tracking-wide text-[#4A4A4A]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#F25C2A] animate-pulse" />
            A marketplace for education &amp; professional fee payments
          </div>

          <h1 className="mt-7 font-serif font-light text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tighter text-[#0A0A0A]">
            One marketplace.
            <br />
            Education fees,
            <br />
            <em className="italic text-[#F25C2A] font-normal">professional services</em>,
            <br />
            travel &amp; more.
          </h1>

          <p className="mt-7 text-lg lg:text-xl text-[#4A4A4A] max-w-xl leading-relaxed">
            EMDpay is a marketplace where payers settle high-value education
            and professional service bills, and discover flight, hotel and bus
            bookings — plus compare and apply for credit cards from leading
            banks. All in one app.
          </p>

          <div className="mt-9 flex flex-wrap items-center gap-3">
            <PlayBadge testId={LANDING.heroInstallCta} variant="dark" />
            <a
              href="#how"
              data-testid={LANDING.heroSecondaryCta}
              className="inline-flex items-center gap-2 rounded-2xl border border-[#E5E5E0] bg-white px-5 py-4 text-sm font-semibold text-[#0A0A0A] hover:border-[#0A0A0A] transition-all"
            >
              See how it works
              <ArrowUpRight size={16} />
            </a>
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-6 text-sm text-[#4A4A4A]">
            <div className="flex items-center gap-1.5">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} size={16} className="fill-[#F25C2A] text-[#F25C2A]" />
              ))}
              <span className="ml-1 font-semibold text-[#0A0A0A]">4.6</span>
              <span>· Play Store</span>
            </div>
            <span className="hidden sm:inline-block h-4 w-px bg-[#E5E5E0]" />
            <div className="flex items-center gap-2">
              <ShieldCheck size={16} className="text-[#0A0A0A]" />
              Verified sellers · Secure payments
            </div>
          </div>
        </motion.div>

        {/* Right phone mockup */}
        <motion.div
          className="lg:col-span-5 relative"
          data-testid={LANDING.heroPhoneMockup}
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
        >
          <div className="relative mx-auto w-[280px] sm:w-[320px] lg:w-full lg:max-w-[380px] aspect-[9/19] animate-float">
            {/* Phone frame */}
            <div className="absolute inset-0 rounded-[3rem] bg-[#0A0A0A] shadow-[0_30px_80px_-30px_rgba(0,0,0,0.4)] p-3">
              <div className="relative h-full w-full rounded-[2.4rem] bg-white overflow-hidden">
                {/* Notch */}
                <div className="absolute top-2 left-1/2 -translate-x-1/2 h-5 w-24 bg-[#0A0A0A] rounded-full z-10" />
                <img
                  src={APP_SCREENSHOTS.home}
                  alt="EMDpay home screen"
                  className="absolute inset-0 h-full w-full object-cover object-top"
                />
              </div>
            </div>

            {/* Floating chip 1 */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.8, duration: 0.6 }}
              className="absolute -left-4 sm:-left-12 top-20 bg-white border border-[#E5E5E0] rounded-2xl px-3 py-2.5 shadow-[0_10px_30px_-15px_rgba(0,0,0,0.2)] flex items-center gap-2"
            >
              <span className="h-8 w-8 rounded-full bg-emerald-50 flex items-center justify-center">
                <Sparkles size={14} className="text-emerald-600" />
              </span>
              <div className="leading-tight">
                <p className="text-[10px] text-[#737373]">Verified educator</p>
                <p className="text-sm font-semibold">KYC complete</p>
              </div>
            </motion.div>

            {/* Floating chip 2 */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1.0, duration: 0.6 }}
              className="absolute -right-2 sm:-right-10 bottom-24 bg-[#0A0A0A] text-white rounded-2xl px-3 py-2.5 shadow-[0_10px_30px_-15px_rgba(0,0,0,0.4)] flex items-center gap-2"
            >
              <span className="h-8 w-8 rounded-full bg-white/10 flex items-center justify-center text-[#F25C2A]">
                ✓
              </span>
              <div className="leading-tight">
                <p className="text-[10px] opacity-70">Settled to seller</p>
                <p className="text-sm font-semibold">Direct to bank</p>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
